import React, { useState, useEffect } from "react";
import { empleadosService } from "../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  Table,
  Form,
  Button,
  Pagination,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { FaSearch, FaEye, FaBell, FaUpload, FaFilter } from "react-icons/fa";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 8;

// Función para formatear el estado del empleado
// Si ESTADO es 1, muestra "Validado", en caso contrario "No Validado"
const formatEmployeeStatus = (estado) => {
  return estado == 1 ? "Validado" : "No Validado";
};

// Función para obtener la clase CSS del estado del empleado
const getEmployeeStatusClass = (estado) => {
  return estado == 1 ? "bg-success text-white" : "bg-warning text-white";
};

// Función para procesar y mostrar los factores factoriales
const renderFactoriales = (factoriales) => {
  if (!factoriales || factoriales === '' || factoriales === null) {
    return null;
  }

  // Separar por ";" y filtrar valores vacíos
  const factores = factoriales.split(';').filter(factor => factor.trim() !== '');

  if (factores.length === 0) {
    return null;
  }

  return (
    <div className="d-flex flex-wrap gap-1">
      {factores.map((factor, index) => (
        <Badge key={index} variant="info" className="text-white">
          {factor.trim()}
        </Badge>
      ))}
    </div>
  );
};

const EmpleadosList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [empleados, setEmpleados] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false); // Control de filtros avanzados
  const [statusFilter, setStatusFilter] = useState("-1"); // Filtro de estado
  // Inicializar el filtro de estado de documentos desde la URL si existe
  const [documentStatusFilter, setDocumentStatusFilter] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const docStatus = urlParams.get('documentStatus');
    return docStatus !== null ? docStatus : "-1";
  }); // Filtro de estado de documentos
  const [selectedRelacionesLaborales, setSelectedRelacionesLaborales] = useState([]); // Relaciones laborales seleccionadas
  const [relacionesLaborales, setRelacionesLaborales] = useState([]); // Lista de relaciones laborales
  const [loadingRelacionesLaborales, setLoadingRelacionesLaborales] = useState(false); // Estado de carga
  const [showRelacionesLaboralesDropdown, setShowRelacionesLaboralesDropdown] = useState(false); // Control del dropdown de relaciones laborales
  const [departamentos, setDepartamentos] = useState([]); // Lista de departamentos
  const [selectedDepartamento, setSelectedDepartamento] = useState("-1"); // Departamento seleccionado
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false); // Estado de carga de departamentos
  const [selectedDocuments, setSelectedDocuments] = useState([]); // Documentos seleccionados
  const [showDocumentsDropdown, setShowDocumentsDropdown] = useState(false); // Control del dropdown de documentos
  const [tiposDocumentos, setTiposDocumentos] = useState([]); // Tipos de documentos cargados del backend
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [activoFilter, setActivoFilter] = useState("1"); // Filtro de activo (1=Sí, 0=No, -1=Todos, por defecto "1") // Estado de carga de tipos de documentos
  const [errorDocumentTypes, setErrorDocumentTypes] = useState(null); // Estado de error para tipos de documentos
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    pages: 1,
  });

  // Leer el parámetro documentStatus de la URL al montar el componente
  useEffect(() => {
    const documentStatusParam = searchParams.get('documentStatus');
    if (documentStatusParam !== null) {
      setDocumentStatusFilter(documentStatusParam);
      setShowAdvancedFilters(true); // Mostrar los filtros avanzados automáticamente
      setPage(1); // Resetear a la primera página
      // Limpiar el parámetro de la URL después de leerlo
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('documentStatus');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    console.log('=== useEffect ejecutado - filtros cambiaron ===');
    console.log('statusFilter:', statusFilter);
    console.log('documentStatusFilter:', documentStatusFilter);
    console.log('selectedRelacionesLaborales:', selectedRelacionesLaborales);
    console.log('selectedDocuments:', selectedDocuments);
    console.log('selectedDepartamento:', selectedDepartamento);
    fetchEmpleados();
    // eslint-disable-next-line
  }, [page, orderBy, statusFilter, documentStatusFilter, selectedRelacionesLaborales, selectedDocuments, selectedDepartamento, activoFilter]);

  // Efecto para cargar los tipos de documentos al montar el componente
  useEffect(() => {
    fetchTiposDocumentos();
    fetchRelacionesLaborales();
    fetchDepartamentos();
  }, []);

  // Efecto para monitorear cambios en tiposDocumentos
  useEffect(() => {
    console.log('=== tiposDocumentos cambió ===');
    console.log('Nuevo tiposDocumentos:', tiposDocumentos);
    console.log('Cantidad de tipos:', tiposDocumentos.length);
  }, [tiposDocumentos]);

  // Efecto para cerrar el dropdown de documentos cuando se haga clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDocumentsDropdown && !event.target.closest('.documents-dropdown-container')) {
        setShowDocumentsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDocumentsDropdown]);

  const fetchEmpleados = async () => {
    setLoading(true);
    try {
      const params = {
        search,
        page,
        limit: PAGE_SIZE,
        orderBy,
        statusFilter, // Agregar el filtro de estado
        documentStatusFilter, // Agregar el filtro de estado de documentos
        selectedRelacionesLaborales, // Agregar las relaciones laborales seleccionadas
        selectedDocuments, // Agregar los documentos seleccionados
        selectedDepartamento, // Agregar el departamento seleccionado
        activoFilter, // Agregar el filtro de activo
      };

      console.log('=== EJECUTANDO fetchEmpleados ===');
      console.log('Parámetros enviados al backend:', params);

      const res = await empleadosService.buscar(params);
      console.log('Respuesta del backend:', res.data);

      setEmpleados(res.data.empleados);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      } else {
        setPagination((prev) => ({
          ...prev,
          total: res.data.empleados.length,
          pages: 1,
        }));
      }
    } catch (err) {
      console.error('Error en fetchEmpleados:', err);
      setEmpleados([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar los tipos de documentos desde el backend
  const fetchTiposDocumentos = async () => {
    setLoadingDocumentTypes(true);
    setErrorDocumentTypes(null);
    try {
      console.log('=== EJECUTANDO fetchTiposDocumentos ===');
      console.log('Usuario autenticado:', localStorage.getItem('user'));

      const res = await empleadosService.getDocumentTypes();
      console.log('Respuesta del backend:', res.data);

      if (res.data.success && res.data.documentTypes) {
        console.log('Tipos de documentos encontrados:', res.data.documentTypes);
        setTiposDocumentos(res.data.documentTypes);
      } else {
        const errorMsg = res.data.message || 'Error al cargar tipos de documentos';
        console.error('Error al cargar tipos de documentos:', errorMsg);
        setErrorDocumentTypes(errorMsg);
        setTiposDocumentos([]);
      }
    } catch (err) {
      console.error('Error completo:', err);
      const errorMsg = err.response?.data?.message || 'Error de conexión al cargar tipos de documentos';
      console.error('Error al cargar tipos de documentos:', errorMsg);
      setErrorDocumentTypes(errorMsg);
      setTiposDocumentos([]);
    } finally {
      setLoadingDocumentTypes(false);
    }
  };

  // Función para cargar las relaciones laborales desde el backend
  const fetchRelacionesLaborales = async () => {
    setLoadingRelacionesLaborales(true);
    try {
      console.log('=== EJECUTANDO fetchRelacionesLaborales ===');
      const res = await empleadosService.getRelacionLaboral(null);
      console.log('Respuesta del backend relaciones laborales:', res.data);

      if (res.data.relacionesLaborales) {
        console.log('Relaciones laborales encontradas:', res.data.relacionesLaborales);
        setRelacionesLaborales(res.data.relacionesLaborales);
      } else {
        setRelacionesLaborales([]);
      }
    } catch (err) {
      console.error('Error al cargar relaciones laborales:', err);
      setRelacionesLaborales([]);
    } finally {
      setLoadingRelacionesLaborales(false);
    }
  };

  // Función para cargar los departamentos desde el backend
  const fetchDepartamentos = async () => {
    setLoadingDepartamentos(true);
    try {
      console.log('=== EJECUTANDO fetchDepartamentos ===');
      const res = await empleadosService.getDepartamentos(null);
      console.log('Respuesta del backend departamentos:', res.data);

      if (res.data.departamentos) {
        console.log('Departamentos encontrados:', res.data.departamentos);
        setDepartamentos(res.data.departamentos);
      } else {
        setDepartamentos([]);
      }
    } catch (err) {
      console.error('Error al cargar departamentos:', err);
      setDepartamentos([]);
    } finally {
      setLoadingDepartamentos(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEmpleados();
  };

  const handleOrder = (colIdx) => {
    setOrderBy(colIdx);
    setPage(1);
  };

  // Importar empleados
  const handleImportEmployees = async () => {
    try {
      setLoading(true);
      const response = await empleadosService.importEmployees();


      if (response.data.success) {
        toast.success(`Empleados importados correctamente.`);
        // Recargar la lista de empleados después de la importación
        fetchEmpleados();
      } else {
        toast.error(response.data.message || "Error al importar empleados");
      }
    } catch (error) {
      console.error('Error importando empleados:', error);
      toast.error(
        error.response?.data?.message ||
        "Error al importar empleados. Verifique la conexión con la base de datos."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h4>Empleados</h4>
        <Button
          variant="success"
          onClick={handleImportEmployees}
          disabled={loading}
          className="d-flex align-items-center gap-2"
        >
          <FaUpload />
          {loading ? "Importando..." : "Import Employees"}
        </Button>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSearch} className="mb-3">
          <div className="d-flex gap-2 align-items-start">
            <InputGroup style={{ flex: 1 }}>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre, email, teléfono o DNI..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Button type="submit" variant="primary">
                <FaSearch />
              </Button>
            </InputGroup>
            <Button
              variant="outline-secondary"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="d-flex align-items-center gap-2"
            >
              <FaFilter />
              Filtros {showAdvancedFilters ? '▼' : '▶'}
            </Button>
          </div>
        </Form>

        {/* Filtros Avanzados */}
        {showAdvancedFilters && (
          <div className="mb-3 p-3 border rounded bg-light">
            <h6 className="mb-3">Filtros Avanzados</h6>
            {/* Primera fila: Estado de Validación, Estado de Documentos, Documentos de Trabajadores Validados */}
            <div className="row g-3">
              <div className="col-md-2">
                <Form.Group>
                  <Form.Label>Estado de Validación</Form.Label>
                  <Form.Select
                    value={statusFilter || "-1"}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="-1">Todos los Estados</option>
                    <option value="1">Validado</option>
                    <option value="2">No Validado</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-2">
                <Form.Group>
                  <Form.Label>Activo</Form.Label>
                  <Form.Select
                    value={activoFilter || "1"}
                    onChange={(e) => {
                      setActivoFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="-1">Todos</option>
                    <option value="1">Sí</option>
                    <option value="0">No</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-2">
                <Form.Group>
                  <Form.Label>Estado de Documentos</Form.Label>
                  <Form.Select
                    value={documentStatusFilter || "-1"}
                    onChange={(e) => {
                      setDocumentStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="-1">Todos</option>
                    <option value="0">Válidos</option>
                    <option value="1">No Válidos</option>
                    <option value="2">Sin Presentar</option>
                    <option value="3">Caducidad Próxima</option>
                    <option value="4">Caducados</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-5">
                <Form.Group>
                  <Form.Label>
                    Documentos de Trabajadores Validados
                  </Form.Label>

                  {/* Multiselector personalizado */}
                  <div className="position-relative documents-dropdown-container">
                    {/* Botón del desplegable */}
                    <Button
                      variant="outline-secondary"
                      className="w-100 text-start d-flex justify-content-between align-items-center"
                      onClick={() => setShowDocumentsDropdown(!showDocumentsDropdown)}
                      style={{ minHeight: '40px' }}
                    >
                      <div className="d-flex flex-wrap gap-1 align-items-center flex-grow-1">
                        {selectedDocuments.length === 0 ? (
                          <span className="text-muted">Selecciona documentos...</span>
                        ) : (
                          selectedDocuments.map((documentId) => {
                            const documento = tiposDocumentos.find(d => (d.ID || d.id) === documentId);

                            return (
                              <span
                                key={documentId}
                                className="badge bg-primary d-flex align-items-center gap-1 me-1"
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '0.75em',
                                  padding: '0.35em 0.6em',
                                  lineHeight: '1.2'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // Evitar que se abra el dropdown
                                  setSelectedDocuments(prev => prev.filter(d => d !== documentId));
                                }}
                              >
                                {documento ? (documento.NOMBRE || documento.nombre) : `Documento ${documentId}`}
                                <span
                                  className="ms-1"
                                  style={{
                                    fontSize: '0.9em',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    lineHeight: '1'
                                  }}
                                >
                                  ×
                                </span>
                              </span>
                            );
                          })
                        )}
                      </div>
                      <FontAwesomeIcon
                        icon={showDocumentsDropdown ? faChevronUp : faChevronDown}
                        className="ms-2"
                      />
                    </Button>

                    {/* Dropdown con opciones */}
                    {showDocumentsDropdown && (
                      <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                        {loadingDocumentTypes ? (
                          <div className="px-3 py-2 text-muted">
                            <div className="d-flex align-items-center">
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Cargando...</span>
                              </div>
                              Cargando tipos de documentos...
                            </div>
                          </div>
                        ) : tiposDocumentos.length > 0 ? (
                          tiposDocumentos.map((documento) => (
                            <div
                              key={documento.ID || documento.id}
                              className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                const documentId = documento.ID || documento.id;
                                const isSelected = selectedDocuments.includes(documentId);
                                if (isSelected) {
                                  setSelectedDocuments(prev => prev.filter(d => d !== documentId));
                                } else {
                                  setSelectedDocuments(prev => [...prev, documentId]);
                                }
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <div className="form-check me-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectedDocuments.includes(documento.ID || documento.id)}
                                  onChange={() => { }} // Manejado por onClick del div
                                />
                              </div>
                              <span>{documento.NOMBRE || documento.nombre}</span>
                            </div>
                          ))
                        ) : errorDocumentTypes ? (
                          <div className="px-3 py-2 text-danger">
                            <div className="d-flex align-items-center">
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              {errorDocumentTypes}
                            </div>
                            <button
                              className="btn btn-sm btn-outline-primary mt-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchTiposDocumentos();
                              }}
                            >
                              Reintentar
                            </button>
                          </div>
                        ) : (
                          <div className="px-3 py-2 text-muted">
                            No hay tipos de documentos disponibles
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <Form.Text className="text-muted">
                    Haz clic en el desplegable para seleccionar múltiples documentos
                  </Form.Text>
                </Form.Group>
              </div>
            </div>

            {/* Segunda fila: Departamento, Relación Laboral, Botón Limpiar */}
            <div className="row g-3 mt-2">
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label>Departamento</Form.Label>
                  <Form.Select
                    value={selectedDepartamento || "-1"}
                    onChange={(e) => {
                      setSelectedDepartamento(e.target.value);
                      setPage(1);
                    }}
                    disabled={loadingDepartamentos}
                  >
                    <option value="-1">Todos los Departamentos</option>
                    {departamentos.map((depto) => (
                      <option key={depto.ID} value={depto.ID}>
                        {depto.NOMBRE}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Relación Laboral</Form.Label>
                  {/* Multiselector personalizado */}
                  <div className="position-relative relaciones-laborales-dropdown-container">
                    {/* Botón del desplegable */}
                    <Button
                      variant="outline-secondary"
                      className="w-100 text-start d-flex justify-content-between align-items-center"
                      onClick={() => setShowRelacionesLaboralesDropdown(!showRelacionesLaboralesDropdown)}
                      style={{ minHeight: '40px' }}
                    >
                      <div className="d-flex flex-wrap gap-1 align-items-center flex-grow-1">
                        {selectedRelacionesLaborales.length === 0 ? (
                          <span className="text-muted">Selecciona relaciones laborales...</span>
                        ) : (
                          selectedRelacionesLaborales.map((relacionId) => {
                            const relacion = relacionesLaborales.find(r => (r.ID || r.id) === relacionId);

                            return (
                              <span
                                key={relacionId}
                                className="badge bg-primary d-flex align-items-center gap-1 me-1"
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '0.75em',
                                  padding: '0.35em 0.6em',
                                  lineHeight: '1.2'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // Evitar que se abra el dropdown
                                  setSelectedRelacionesLaborales(prev => prev.filter(r => r !== relacionId));
                                }}
                              >
                                {relacion ? (relacion.TEXTO || relacion.texto) : `Relación ${relacionId}`}
                                <span
                                  className="ms-1"
                                  style={{
                                    fontSize: '0.9em',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    lineHeight: '1'
                                  }}
                                >
                                  ×
                                </span>
                              </span>
                            );
                          })
                        )}
                      </div>
                      <FontAwesomeIcon
                        icon={showRelacionesLaboralesDropdown ? faChevronUp : faChevronDown}
                        className="ms-2"
                      />
                    </Button>

                    {/* Dropdown con opciones */}
                    {showRelacionesLaboralesDropdown && (
                      <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                        {loadingRelacionesLaborales ? (
                          <div className="px-3 py-2 text-muted">
                            <div className="d-flex align-items-center">
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Cargando...</span>
                              </div>
                              Cargando relaciones laborales...
                            </div>
                          </div>
                        ) : relacionesLaborales.length > 0 ? (
                          relacionesLaborales.map((relacion) => (
                            <div
                              key={relacion.ID || relacion.id}
                              className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                const relacionId = relacion.ID || relacion.id;
                                const isSelected = selectedRelacionesLaborales.includes(relacionId);
                                if (isSelected) {
                                  setSelectedRelacionesLaborales(prev => prev.filter(r => r !== relacionId));
                                } else {
                                  setSelectedRelacionesLaborales(prev => [...prev, relacionId]);
                                }
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              <div className="form-check me-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={selectedRelacionesLaborales.includes(relacion.ID || relacion.id)}
                                  onChange={() => { }} // Manejado por onClick del div
                                />
                              </div>
                              <span>{relacion.TEXTO || relacion.texto}</span>
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-muted">
                            No hay relaciones laborales disponibles
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <Form.Text className="text-muted">
                    Haz clic en el desplegable para seleccionar múltiples relaciones laborales
                  </Form.Text>
                </Form.Group>
              </div>
              <div className="col-md-5 d-flex align-items-end justify-content-end">
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("-1");
                    setDocumentStatusFilter("-1");
                    setSelectedRelacionesLaborales([]);
                    setSelectedDocuments([]);
                    setSelectedDepartamento("-1");
                    setActivoFilter("1");
                    setPage(1);
                    fetchEmpleados();
                  }}
                >
                  Limpiar Filtros
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Vista de tabla para desktop */}
        <div className="d-none d-md-block">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th
                  onClick={() => handleOrder(0)}
                  style={{ cursor: "pointer" }}
                >
                  EMPLEADO
                </th>
                <th
                  onClick={() => handleOrder(1)}
                  style={{ cursor: "pointer" }}
                >
                  EMAIL
                </th>
                <th
                  onClick={() => handleOrder(2)}
                  style={{ cursor: "pointer" }}
                >
                  TELÉFONO
                </th>
                <th
                  onClick={() => handleOrder(3)}
                  style={{ cursor: "pointer" }}
                >
                  DNI
                </th>
                <th>DEPARTAMENTO</th>
                <th
                  onClick={() => handleOrder(4)}
                  style={{ cursor: "pointer" }}
                >
                  ESTADO
                </th>
                <th>ACTIVO</th>
                <th>SEGUIMIENTO FACTORIAL</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {empleados.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center">
                    No hay empleados
                  </td>
                </tr>
              )}
              {empleados.map((emp) => (
                <tr key={emp.ID}>
                  <td>{emp.EMPLEADO}</td>
                  <td>{emp.EMAIL}</td>
                  <td>{emp.TELEFONO_DEFECTO}</td>
                  <td>{emp.DNI}</td>
                  <td>{emp.DEPARTAMENTO || "-"}</td>
                  <td>
                    <Badge className={getEmployeeStatusClass(emp.ESTADO)}>
                      {formatEmployeeStatus(emp.ESTADO)}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={emp.ACTIVO === 1 || emp.ACTIVO === true ? "success" : "secondary"}>
                      {emp.ACTIVO === 1 || emp.ACTIVO === true ? "Sí" : "No"}
                    </Badge>
                  </td>
                  <td style={{ width: '285px' }}>
                    {renderFactoriales(emp.FACTORIALES)}
                  </td>
                  <td>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() =>
                        navigate(`/empleado-detail?empleado_id=${emp.ID}`)
                      }
                      title="Ver detalle empleado"
                    >
                      <FaEye />
                    </Button>

                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {/* Vista de cards para móvil */}
        <div className="d-md-none">
          {empleados.length === 0 ? (
            <div className="text-center py-4">No hay empleados</div>
          ) : (
            <div className="gastos-cards">
              {empleados.map((emp) => (
                <Card
                  key={emp.ID}
                  className={`mb-3 gasto-card ${emp.ESTADO === 1 ? "border-success" : "border-warning"}`}
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{emp.EMPLEADO}</h6>
                        <small className="text-muted">ID: {emp.ID}</small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Badge className={getEmployeeStatusClass(emp.ESTADO)}>
                          {formatEmployeeStatus(emp.ESTADO)}
                        </Badge>
                        <Badge bg={emp.ACTIVO === 1 || emp.ACTIVO === true ? "success" : "secondary"}>
                          {emp.ACTIVO === 1 || emp.ACTIVO === true ? "Sí" : "No"}
                        </Badge>
                      </div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-12">
                        <small className="text-muted d-block">Email:</small>
                        <span>{emp.EMAIL}</span>
                      </div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <small className="text-muted d-block">Teléfono:</small>
                        <span>{emp.TELEFONO_DEFECTO}</span>
                      </div>
                      <div className="col-6">
                        <small className="text-muted d-block">DNI:</small>
                        <span>{emp.DNI}</span>
                      </div>
                    </div>
                    {(emp.DEPARTAMENTO && emp.DEPARTAMENTO !== '') && (
                      <div className="row g-2 mb-2">
                        <div className="col-12">
                          <small className="text-muted d-block">Departamento:</small>
                          <span>{emp.DEPARTAMENTO}</span>
                        </div>
                      </div>
                    )}
                    {emp.FACTORIALES && emp.FACTORIALES !== '' && (
                      <div className="row g-2 mb-2">
                        <div className="col-12">
                          <small className="text-muted d-block">Factoriales:</small>
                          {renderFactoriales(emp.FACTORIALES)}
                        </div>
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() =>
                          navigate(`/empleado-detail?empleado_id=${emp.ID}`)
                        }
                        title="Ver detalle empleado"
                      >
                        <FaEye />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
        {/* Paginación mejorada */}
        {pagination.pages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.First
                onClick={() => setPage(1)}
                disabled={pagination.page === 1}
              />
              <Pagination.Prev
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
              />
              {/* Páginas dinámicas centradas en la actual */}
              {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                .filter(
                  (pageNum) =>
                    pageNum === 1 ||
                    pageNum === pagination.pages ||
                    (pageNum >= pagination.page - 2 &&
                      pageNum <= pagination.page + 2)
                )
                .map((pageNum, idx, arr) => {
                  // Añadir puntos suspensivos si hay salto
                  if (idx > 0 && pageNum - arr[idx - 1] > 1) {
                    return [
                      <Pagination.Ellipsis
                        key={`ellipsis-${pageNum}`}
                        disabled
                      />,
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>,
                    ];
                  }
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={pageNum === pagination.page}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
              <Pagination.Next
                onClick={() =>
                  setPage((p) => Math.min(pagination.pages, p + 1))
                }
                disabled={pagination.page === pagination.pages}
              />
              <Pagination.Last
                onClick={() => setPage(pagination.pages)}
                disabled={pagination.page === pagination.pages}
              />
            </Pagination>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default EmpleadosList;